/**
 * Value-returning per-handler auth wrapper for unary RPCs. Drop-in
 * replacement for `withAuthUnary` (callback-style) that matches the
 * shape `@nodii/grpc-interceptors`' `createStandardServerStack`
 * composer expects for slot 3 (auth) in the locked 13-stack:
 *
 *   `UnaryHandler<Req, Res> = (call) => Promise<Res>`
 *
 * Behavior is otherwise identical to `withAuthUnary`:
 *   - Reads `authorization` from `call.metadata`
 *   - Strips `Bearer ` prefix
 *   - Calls `verifyS2SToken` with the configured replayStore +
 *     options (so jti-replay protection applies automatically)
 *   - Attaches `S2SAuthContext` to `call.auth`
 *   - Enforces per-method required scope from
 *     `getGrpcAuthConfig().requiredScopesByMethod`
 *   - Calls the inner handler and returns its value
 *
 * On failure, throws a `ServiceError`-shaped error with the appropriate
 * `status` code — the 13-stack `errorMap` interceptor (slot 12)
 * translates these into wire-level gRPC status responses.
 *
 * Per R1: this wrapper reads the configured singleton, which itself
 * throws `ConfigError` if `configureGrpcAuth(...)` did not run.
 * No silent fallthrough to "skip auth" — a missing config is a hard
 * crash, not a passthrough.
 */
import { status } from "@grpc/grpc-js";
import { getGrpcAuthConfig } from "../configure";
import type {
  S2SAuthContext,
  ServerUnaryCallWithAuth,
} from "../types/auth-context";
import {
  UserActorEnvelopeError,
  X_USER_CONTEXT_HEADER,
  decodeUserActorEnvelope,
} from "../types/user-actor";
import { userContextStore } from "../user-context-als";
import { verifyS2SToken } from "../verify/s2s-verify";
import { grpcError, grpcStatusForVerifyError } from "./grpc-error";
import { requireScope } from "./require-scope";

export type UnaryValueHandler<Req, Res> = (
  call: ServerUnaryCallWithAuth<Req, Res>,
) => Promise<Res>;

export type WithAuthUnaryValueOptions = {
  /**
   * If provided and returns `true`, auth is skipped entirely. Use ONLY
   * for explicit health-check / reflection methods. Parity with the
   * legacy `withAuthUnary({ skipAuth })` escape hatch.
   */
  skipAuth?: () => boolean;
};

/**
 * Wraps a handler with S2S auth verification + per-method scope
 * enforcement. The returned function is the call-site handler that
 * the 13-stack composer plugs into slot 3.
 *
 * @param methodName - the fully-qualified gRPC method
 *   (`<package>.<Service>/<Method>`); used as the key into
 *   `requiredScopesByMethod`.
 * @param handler - the value-returning business-logic handler.
 */
export function withAuthUnaryValue<Req, Res>(
  methodName: string,
  handler: UnaryValueHandler<Req, Res>,
  opts: WithAuthUnaryValueOptions = {},
): UnaryValueHandler<Req, Res> {
  return async (call: ServerUnaryCallWithAuth<Req, Res>): Promise<Res> => {
    if (opts.skipAuth?.()) {
      return handler(call);
    }

    const cfg = getGrpcAuthConfig();

    const authHeader = call.metadata.get("authorization")[0];
    if (typeof authHeader !== "string" || !authHeader.startsWith("Bearer ")) {
      throw grpcError(
        status.UNAUTHENTICATED,
        "Missing or invalid Authorization header",
      );
    }
    const token = authHeader.slice("Bearer ".length);

    // This is the ONLY TS server entry point that actually wires
    // `cfg.replayStore`, so it is the one that can throw
    // `S2SReplayStoreUnavailable` — and it had NO try/catch at all, so the
    // error escaped to grpc-js and surfaced as UNKNOWN(2). Neither UNKNOWN nor
    // the UNAUTHENTICATED the sibling entry points emit tells a caller to
    // retry, which is the entire point of the typed outage error.
    let verified: Awaited<ReturnType<typeof verifyS2SToken>>;
    try {
      verified = await verifyS2SToken(token, {
        expectedAudience: cfg.expectedAudience,
        expectedIssuer: cfg.expectedIssuer,
        maxClockSkewSeconds: cfg.maxClockSkewSeconds,
        // Hub decision D845: the store reaches the verifier ONLY when replay
        // enforcement is explicitly on. (Before 0.17.0 the flag gated nothing but
        // the boot-time ConfigError — a wired store always enforced.)
        replayStore: cfg.enforceJtiReplay ? cfg.replayStore : undefined,
      });
    } catch (err) {
      throw grpcError(
        grpcStatusForVerifyError(err),
        err instanceof Error ? err.message : "Invalid token",
      );
    }

    call.auth = {
      serviceId: verified.serviceId,
      serviceName: verified.serviceName,
      scopes: verified.scopes,
      instanceId: verified.instanceId,
    } satisfies S2SAuthContext;

    // A2 / D454 — DECODE ON RECEIVE (hop-gated). Only reached inside the
    // S2S-verified block. If an inbound `x-user-context` header is present,
    // decode it onto `call.auth.userActor` so the downstream cross-tenant
    // fence (`enrichAuthContext`) has the authoritative user tenant. A
    // malformed envelope on an AUTHENTICATED hop is an attack, not a no-op →
    // fail closed with INVALID_ARGUMENT.
    const rawUserContext = call.metadata.get(X_USER_CONTEXT_HEADER)[0];
    if (typeof rawUserContext === "string" && rawUserContext.length > 0) {
      try {
        call.auth.userActor = decodeUserActorEnvelope(rawUserContext);
      } catch (decodeErr) {
        const detail =
          decodeErr instanceof UserActorEnvelopeError
            ? decodeErr.message
            : "x-user-context: malformed user-context envelope";
        throw grpcError(status.INVALID_ARGUMENT, detail);
      }
    }

    const requiredScope = cfg.requiredScopesByMethod[methodName];
    if (requiredScope) {
      requireScope(call.auth, requiredScope);
    } else if (cfg.denyUnlistedMethods) {
      // F1.5 default-DENY (request e95b3100 / D429): an unlisted method is
      // NOT skipped on token-validity alone — it is rejected. Closes the
      // gRPC default-ALLOW class structurally.
      throw grpcError(
        status.PERMISSION_DENIED,
        `Method not bound to a required scope (denyUnlistedMethods): ${methodName}`,
      );
    }

    // A2 / D454 — SERVER-SIDE CAPTURE. When an inbound `x-user-context` is
    // present, run the handler inside the ambient store so the client auth
    // interceptor on any outgoing S2S hop re-attaches the SAME raw header
    // (user identity propagates A→B). Absent header → run normally.
    if (typeof rawUserContext === "string" && rawUserContext.length > 0) {
      return userContextStore.run(rawUserContext, () => handler(call));
    }
    return handler(call);
  };
}
